<template>
    <div class="ef-login-page">
        <div class="ef-login-page__inner">
            <div class="ef-login-logo">
                <img
                    src="/elite-logo-white.png"
                    alt="ELITE fitness"
                    class="ef-login-logo__img"
                    width="280"
                    height="120"
                    decoding="async"
                />
            </div>
            <p class="ef-login-page__hint">Accedi inserendo le tue credenziali.</p>

            <form class="ef-login-page__form" autocomplete="on" name="login" @submit.prevent="login">
                <div class="ef-login-page__field">
                    <span class="ef-login-page__field-icon" aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                        </svg>
                    </span>
                    <input
                        id="email"
                        v-model="email"
                        type="text"
                        name="email"
                        autocomplete="email"
                        placeholder="Email"
                        autocapitalize="off"
                        autocorrect="off"
                        spellcheck="false"
                        required
                    />
                </div>

                <div class="ef-login-page__field ef-login-page__field--password">
                    <span class="ef-login-page__field-icon" aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                        </svg>
                    </span>
                    <input
                        id="password"
                        ref="passwordInput"
                        v-model="password"
                        :type="showPassword ? 'text' : 'password'"
                        name="password"
                        autocomplete="current-password"
                        placeholder="Password"
                        required
                    />
                    <button
                        type="button"
                        class="ef-login-page__toggle"
                        :aria-label="showPassword ? 'Nascondi password' : 'Mostra password'"
                        @click="showPassword = !showPassword"
                    >
                        <svg v-if="showPassword" xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <svg v-else xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                        </svg>
                    </button>
                </div>

                <p class="ef-login-page__forgot">
                    <router-link to="/password-reset">Ho dimenticato la password.</router-link>
                </p>

                <p v-if="errorMessage" class="ef-login-page__error">{{ errorMessage }}</p>

                <button type="submit" class="ef-login-page__submit" :disabled="isLoading">
                    {{ isLoading ? 'Accesso…' : 'Accedi' }}
                </button>
            </form>

            <!-- Accesso alternativo con l'app (QR device-login), solo al kiosk. -->
            <template v-if="isKiosk">
                <div class="ef-login-page__divider"><span>oppure</span></div>
                <button type="button" class="ef-login-page__app" @click="startKioskLogin">
                    Accedi con la tua app
                </button>
                <p v-if="qrError" class="ef-login-page__error">{{ qrError }}</p>
            </template>

            <p class="ef-login-page__footer">
                Non hai un account?
                <router-link
                    :to="{ path: '/register', query: { returnUrl: $route.query.returnUrl || undefined } }"
                    class="ef-login-page__footer-link"
                >
                    Registrati
                </router-link>
            </p>
        </div>

        <!-- Modale QR device-login -->
        <div v-if="showQr" class="ef-qr-modal" @click.self="stopKioskLogin">
            <div class="ef-qr-card">
                <h3>Accedi con la tua app</h3>
                <p>Inquadra il QR con l'app Elite Fitness per accedere.</p>
                <img v-if="qrDataUrl" :src="qrDataUrl" alt="QR accesso" class="ef-qr-img" />
                <p class="ef-qr-status">In attesa di conferma…</p>
                <button type="button" class="ef-qr-close" @click="stopKioskLogin">Chiudi</button>
            </div>
        </div>
    </div>
</template>

<script>
import { IS_KIOSK } from '@/config/kiosk';
import QRCode from 'qrcode';

export default {
    data() {
        return {
            email: '',
            password: '',
            errorMessage: '',
            isLoading: false,
            showPassword: false,
            isKiosk: IS_KIOSK,
            showQr: false,
            qrDataUrl: null,
            kioskSessionId: null,
            pollTimer: null,
            qrError: '',
        };
    },
    methods: {
        async login() {
            const pw = this.$refs.passwordInput;
            if (pw) pw.type = 'password';
            this.showPassword = false;

            this.isLoading = true;
            this.errorMessage = '';
            try {
                const response = await fetch('https://api.elitefitness.it/api/auth/login', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        email: this.email,
                        password: this.password,
                    }),
                });

                if (!response.ok) {
                    this.errorMessage = 'Credenziali non valide. Riprova.';
                    return;
                }

                const data = await response.json();
                this.onAuthenticated(data, this.email);
            } catch (error) {
                console.error('Login failed:', error);
                this.errorMessage = 'Si è verificato un errore durante il login. Riprova più tardi.';
            } finally {
                this.isLoading = false;
            }
        },

        // Persiste il token e reindirizza. Condiviso tra login password e QR-login,
        // così l'esito è identico (rispetta returnUrl/action/account).
        onAuthenticated(data, email) {
            if (email) localStorage.setItem('userEmail', email);
            localStorage.setItem('userToken', data.token);
            localStorage.setItem('tokenExpiration', data.expiresIn);

            const returnUrlEncoded = this.$route.query.returnUrl;
            if (returnUrlEncoded) {
                const decoded = decodeURIComponent(returnUrlEncoded);
                const [pathOnly, queryString] = decoded.split('?');
                const params = new URLSearchParams(queryString || '');
                if (!params.has('userToken')) {
                    params.set('userToken', data.token);
                }
                this.$router.push({ path: pathOnly, query: Object.fromEntries(params) });
            } else {
                const action = this.$route.query.action;
                const query = {
                    productId: this.$route.query.productId,
                    variationName: this.$route.query.variationName,
                    discountCode: this.$route.query.discountCode,
                };
                const optionalKeys = [
                    'bypm', 'freeTrial', 'referralCode',
                    'biaTrainerId', 'targetTrainerId', 'personalTrainerId',
                    'requiresBIA', 'requiresTarget', 'requiresStopAndGo', 'requiresMembership', 'requiresRegistration',
                    'mandatoryProductId', 'mandatoryVariation',
                    'upsells',
                ];
                optionalKeys.forEach((k) => {
                    const v = this.$route.query[k];
                    if (v !== undefined) query[k] = v;
                });
                if (action) {
                    this.$router.push({ path: `/${action}`, query });
                } else if (this.isKiosk) {
                    // Al kiosk non esiste una "area account/scarica app": torna alla home.
                    this.$router.push({ path: '/kiosk' });
                } else {
                    this.$router.push({ name: 'account', query });
                }
            }
        },

        // --- Accesso con l'app (QR device-login) --------------------------------
        async startKioskLogin() {
            this.qrError = '';
            try {
                const res = await fetch('https://api.elitefitness.it/api/kiosk/auth/session', { method: 'POST' });
                if (!res.ok) throw new Error('session');
                const { sessionId, code } = await res.json();
                this.kioskSessionId = sessionId;
                this.qrDataUrl = await QRCode.toDataURL('https://app.elitefitness.it/kiosk-login?code=' + encodeURIComponent(code), { width: 320, margin: 1 });
                this.showQr = true;
                this.pollKioskSession();
            } catch (e) {
                this.qrError = 'Impossibile avviare l\'accesso con l\'app. Riprova.';
            }
        },
        pollKioskSession() {
            this.stopKioskPolling();
            this.pollTimer = setInterval(async () => {
                if (!this.kioskSessionId) return;
                try {
                    const r = await fetch('https://api.elitefitness.it/api/kiosk/auth/session/' + encodeURIComponent(this.kioskSessionId));
                    const d = await r.json();
                    if (d.status === 'APPROVED') {
                        this.stopKioskLogin();
                        this.onAuthenticated({ token: d.token, expiresIn: d.expiresIn });
                    } else if (d.status === 'EXPIRED') {
                        this.stopKioskPolling();
                        this.qrError = 'QR scaduto. Genera un nuovo codice.';
                        this.showQr = false;
                    }
                } catch (_) { /* riprova al prossimo tick */ }
            }, 2000);
        },
        stopKioskPolling() {
            if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
        },
        stopKioskLogin() {
            this.stopKioskPolling();
            this.showQr = false;
            this.qrDataUrl = null;
            this.kioskSessionId = null;
        },
    },
    beforeUnmount() {
        this.stopKioskPolling();
    },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&display=swap');

/* Allineato a elitefitness-react-app/src/pages/LoginPage.css */
.ef-login-page {
    --ef-login-blue: #272d82;
    --ef-login-primary: #272d82;
    --ef-login-input-bg: #ffffff;
    --ef-login-muted: rgba(255, 255, 255, 0.82);
    --ef-safe-top: 0px;
    --ef-safe-left: 0px;
    --ef-safe-right: 0px;
    --ef-safe-bottom: 0px;
    margin-top: calc(-1 * var(--ef-safe-top));
    margin-left: calc(-1 * var(--ef-safe-left));
    margin-right: calc(-1 * var(--ef-safe-right));
    min-height: calc(100dvh + var(--ef-safe-top));
    min-height: calc(100svh + var(--ef-safe-top));
    position: relative;
    overflow: hidden;
    background: linear-gradient(
        125deg,
        #172554 0%,
        #1e3a8a 18%,
        #2563eb 38%,
        #1d4ed8 55%,
        #312e81 72%,
        #1e40af 88%,
        #172554 100%
    );
    background-size: 320% 320%;
    animation: ef-login-page-gradient 12s ease-in-out infinite;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: calc(32px + var(--ef-safe-top)) calc(24px + var(--ef-safe-right))
        calc(48px + var(--ef-safe-bottom)) calc(24px + var(--ef-safe-left));
    box-sizing: border-box;
    font-family: 'Montserrat', system-ui, sans-serif;
}

@keyframes ef-login-page-gradient {
    0% {
        background-position: 0% 40%;
    }
    50% {
        background-position: 100% 60%;
    }
    100% {
        background-position: 0% 40%;
    }
}

@media (prefers-reduced-motion: reduce) {
    .ef-login-page {
        animation: none;
        background-size: 100% 100%;
        background-position: 50% 50%;
    }
}

.ef-login-page__inner {
    width: 100%;
    max-width: 380px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 28px;
}

.ef-login-logo {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
}

.ef-login-logo__img {
    display: block;
    width: min(100%, 280px);
    height: auto;
    max-height: min(28dvh, 160px);
    object-fit: contain;
}

.ef-login-page__hint {
    margin: 0;
    text-align: center;
    font-size: 0.9rem;
    color: var(--ef-login-muted);
}

.ef-login-page__form {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.ef-login-page__field {
    position: relative;
    display: flex;
    align-items: center;
    background: var(--ef-login-input-bg);
    border-radius: 14px;
    border: none;
    min-height: 52px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.ef-login-page__field-icon {
    position: absolute;
    left: 14px;
    display: flex;
    color: #9ca3af;
    pointer-events: none;
}

.ef-login-page__field input {
    width: 100%;
    border: none;
    background: transparent;
    padding: 14px 48px 14px 46px;
    font-size: 1rem;
    font-family: inherit;
    color: #1f2937;
    border-radius: 14px;
    outline: none;
}

.ef-login-page__field input::placeholder {
    color: #9ca3af;
}

.ef-login-page__field--password input {
    padding-right: 48px;
}

.ef-login-page__toggle {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 0;
    border: none;
    background: transparent;
    color: #9ca3af;
    cursor: pointer;
    border-radius: 10px;
}

.ef-login-page__toggle:hover {
    color: #6b7280;
}

.ef-login-page__toggle:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
}

.ef-login-page__forgot {
    margin: -4px 0 0;
    text-align: center;
    font-size: 0.88rem;
}

/* Area di tocco di «Ho dimenticato la password.» (devops#464): il testo misurava 201x16px. Il padding la porta a ~45px di
   altezza e il margine negativo, uguale e contrario, restituisce lo spazio: la riga non cresce e il pulsante «Accedi»
   non si sposta. Sopra la riga restano 10px (gap 14 meno il margine di -4 del paragrafo) e sotto 20 (gap 14 più i 6 di
   margine del pulsante): il padding non può superarli, o il bersaglio entrerebbe nel campo password o nel pulsante.
   Ne usa 8 sopra e 16 sotto, così fra il bersaglio e i due vicini resta un po' d'aria (misurata: 2px e 4px). */
.ef-login-page__forgot :deep(a) {
    display: inline-block;
    padding: 8px 12px 16px;
    margin: -8px -12px -16px;
    border: none;
    background: none;
    font: inherit;
    color: rgba(255, 255, 255, 0.95);
    cursor: pointer;
    text-decoration: none;
}

.ef-login-page__forgot :deep(a):hover {
    text-decoration: underline;
}

.ef-login-page__error {
    margin: 0;
    font-size: 0.88rem;
    color: #fecaca;
    text-align: center;
}

.ef-login-page__submit {
    margin-top: 6px;
    width: 100%;
    padding: 15px 20px;
    border: none;
    border-radius: 14px;
    background: #ffffff;
    color: var(--ef-login-primary);
    font-size: 1rem;
    font-weight: 700;
    font-family: inherit;
    cursor: pointer;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.22);
}

.ef-login-page__submit:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.ef-login-page__submit:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 3px;
}

.ef-login-page__footer {
    margin: 0;
    text-align: center;
    font-size: 0.92rem;
    color: rgba(255, 255, 255, 0.92);
}

.ef-login-page__footer :deep(a),
.ef-login-page__footer-link {
    font-weight: 700;
    color: #ffffff;
    text-decoration: none;
    padding: 0;
    border: none;
    background: none;
    font: inherit;
    cursor: pointer;
}

.ef-login-page__footer :deep(a:hover),
.ef-login-page__footer-link:hover {
    text-decoration: underline;
}

/* Area di tocco di «Registrati» (devops#464): il testo misurava 70x17px. Stesso schema di «Torna all'accesso» in
   PasswordReset (devops#458): padding più margine negativo uguale, la riga resta dov'era. Orizzontalmente 4px, quanto
   lo spazio che lo separa da «Non hai un account?», per non coprire il «?». Il selettore ha due classi perché la regola
   `:deep(a)` sopra pesa più di `.ef-login-page__footer-link` da solo e riporta il padding a 0. */
.ef-login-page__footer .ef-login-page__footer-link {
    display: inline-block;
    padding: 14px 4px;
    margin: -14px -4px;
}

/* Accesso con l'app (QR device-login) */
.ef-login-page__divider {
    display: flex;
    align-items: center;
    gap: 12px;
    color: rgba(255, 255, 255, 0.6);
    margin: 18px 0 14px;
    font-size: 0.9rem;
}
.ef-login-page__divider::before,
.ef-login-page__divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.2);
}
.ef-login-page__app {
    width: 100%;
    padding: 12px;
    border: none;
    border-radius: 8px;
    background: #ffc933;
    color: #1a1a1a;
    font-weight: 600;
    font-size: 1.05rem;
    cursor: pointer;
}
.ef-login-page__app:active { background: #e6b52e; }

.ef-qr-modal {
    position: fixed;
    inset: 0;
    z-index: 99999;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.75);
}
.ef-qr-card {
    background: #2e348c;
    color: #fff;
    border-radius: 20px;
    padding: 2.5rem;
    text-align: center;
    max-width: 90vw;
}
.ef-qr-card h3 { font-weight: 700; margin: 0 0 0.5rem; }
.ef-qr-img {
    width: 260px;
    height: 260px;
    background: #fff;
    padding: 10px;
    border-radius: 12px;
    margin: 1rem 0 0.5rem;
}
.ef-qr-status { opacity: 0.85; }
.ef-qr-close {
    margin-top: 1rem;
    background: rgba(255, 255, 255, 0.14);
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 10px 28px;
    font-size: 1rem;
    cursor: pointer;
}
</style>
